import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Button,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import { useNavigate } from 'react-router-dom';

// Components
import TaxProfileSummary from './components/TaxProfileSummary';
import TaxDeadlinesAlert from './components/TaxDeadlinesAlert';
import RequestTaxServiceDialog from './components/RequestTaxServiceDialog';

// simulateDelay retained for future API integration scaffolding
import {
  simulateDelay
} from '../../../mockData/orgDocIndex';

// MockBadge inline component for mock data indication
const MockBadge = () => (
  <Chip
    label="MOCK"
    size="small"
    color="warning"
    sx={{ ml: 1, fontSize: '10px', height: '20px' }}
  />
);

/**
 * Tax Status Page
 * Dashboard view for tax compliance status
 *
 * Features:
 * - Tax profile summary (TRN, VAT status, filing status)
 * - Upcoming deadlines alert
 * - Filing status with due dates
 * - Small Business Relief eligibility
 * - Tax Agent status
 * - Quick action to request services
 *
 * @mock Uses mock data during UI-First phase
 */
const TaxStatusPage = () => {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [taxProfile, setTaxProfile] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [overdueDeadlines, setOverdueDeadlines] = useState([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // TODO: Get actual organization ID from context/auth
  const organizationId = 'org-001';

  // Load data
  // TODO: Replace simulateDelay stub with real API calls:
  //   GET /api/v1/tax/profile?orgId={organizationId}
  //   GET /api/v1/tax/deadlines?orgId={organizationId}&type=upcoming
  //   GET /api/v1/tax/deadlines?orgId={organizationId}&type=overdue
  const loadData = async () => {
    setLoading(true);
    try {
      await simulateDelay(500);

      // Empty defaults — replaced by real API response when available
      setTaxProfile(null);
      setDeadlines([]);
      setOverdueDeadlines([]);
    } catch (error) {
      console.error('Error loading tax data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle request service from deadline alert
  const handleRequestService = (service) => {
    setSelectedService(service);
    setRequestDialogOpen(true);
  };

  // Handle service requested
  const handleServiceRequested = (request) => {
    setRequestDialogOpen(false);
    setSelectedService(null);
    // Navigate to requests page after submitting
    navigate('/CST-TaxServices/requests');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" fontWeight="bold">
              Tax Status
            </Typography>
            <MockBadge />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadData} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<DescriptionOutlined />}
              onClick={() => navigate('/CST-TaxServices/requests')}
            >
              My Requests
            </Button>
            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={() => navigate('/CST-TaxServices/list')}
            >
              Request Service
            </Button>
          </Box>
        </Box>

        {/* Overdue Deadlines Alert */}
        {overdueDeadlines.length > 0 && (
          <TaxDeadlinesAlert
            deadlines={overdueDeadlines}
            onRequestService={handleRequestService}
          />
        )}

        {/* Tax Profile Summary */}
        <TaxProfileSummary
          profile={taxProfile}
          deadlines={deadlines}
        />
      </Box>

      {/* Request Service Dialog */}
      <RequestTaxServiceDialog
        open={requestDialogOpen}
        onClose={() => {
          setRequestDialogOpen(false);
          setSelectedService(null);
        }}
        service={selectedService}
        taxProfile={taxProfile}
        organizationId={organizationId}
        onSubmit={handleServiceRequested}
      />
    </>
  );
};

export default TaxStatusPage;
